import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { racesAPI, picksAPI, leaderboardAPI, tidbitsAPI } from '../services/api';
import RaceCountdown from '../components/RaceCountdown';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upcomingRaces, setUpcomingRaces] = useState([]);
  const [recentPicks, setRecentPicks] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [pinnedTidbits, setPinnedTidbits] = useState([]);
  const [expandedTidbits, setExpandedTidbits] = useState({});
  const [scratchedAlerts, setScratchedAlerts] = useState([]);

  useEffect(() => {
    const loadDashboardData = async () => {
    try {

      // Load upcoming races (limit 3)
      const racesResponse = await racesAPI.getRaces({ upcoming_only: true, limit: 3 });
      if (racesResponse.success) {
        setUpcomingRaces(racesResponse.data.races || []);
      }

      // Load recent picks (limit 5 for display)
      const picksResponse = await picksAPI.getMyPicks({ limit: 5 });
      if (picksResponse.success) {
        setRecentPicks(picksResponse.data.picks || []);
      }

      // Check ALL pending picks for scratched horses (separate fetch, no limit)
      const pendingResponse = await picksAPI.getMyPicks({ status: 'pending', limit: 100 });
      if (pendingResponse.success) {
        const pendingPicks = pendingResponse.data.picks || [];
        if (pendingPicks.length > 0) {
          const uniqueRaceIds = [...new Set(pendingPicks.map(p => p.raceId))];
          const raceResults = await Promise.all(
            uniqueRaceIds.map(id => racesAPI.getRaceById(id).catch(() => null))
          );
          const raceMap = {};
          raceResults.forEach(r => { if (r?.success) raceMap[r.data.raceId] = r.data; });

          const alerts = [];
          for (const pick of pendingPicks) {
            const race = raceMap[pick.raceId];
            if (!race) continue;
            const scratchedHorses = pick.horses?.filter(ph =>
              race.horses?.find(rh => rh.horseId === ph.horseId && rh.scratched)
            ) || [];
            if (scratchedHorses.length > 0) {
              alerts.push({ pick, race, scratchedHorses });
            }
          }
          setScratchedAlerts(alerts);
        }
      }

      // Load user stats
      if (user?.userId) {
        const statsResponse = await leaderboardAPI.getUserStats(user.userId);
        if (statsResponse.success) {
          setUserRank(statsResponse.data.rank);
        }
      }

      // Load pinned tidbits
      const tidbitsResponse = await tidbitsAPI.getAllTidbits({ isPinned: 'true' });
      if (tidbitsResponse.success) {
        setPinnedTidbits(tidbitsResponse.data.tidbits || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
    };
    loadDashboardData();
  }, [user]);

  // Initialize all pinned tidbits as expanded when data loads
  useEffect(() => {
    if (pinnedTidbits.length > 0) {
      const expanded = {};
      pinnedTidbits.forEach(tidbit => {
        expanded[tidbit.tidbitId] = true; // Default to expanded
      });
      setExpandedTidbits(expanded);
    }
  }, [pinnedTidbits]);

  const toggleExpand = (tidbitId) => {
    setExpandedTidbits(prev => ({
      ...prev,
      [tidbitId]: !prev[tidbitId]
    }));
  };

  return (
    <div className="dashboard">
      <h2>Dashboard</h2>

      {/* Scratched horse alerts */}
      {scratchedAlerts.map(({ pick, race, scratchedHorses }) => {
        const deadlinePassed = new Date(race.pickDeadline) < new Date();
        return (
          <div
            key={pick.pickId}
            style={{
              padding: '1rem',
              background: '#ffebee',
              border: '2px solid #f44336',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold', color: '#c62828', fontSize: '1.05rem' }}>
              🚫 Scratched Horse — Action Required
            </p>
            <p style={{ margin: '0.4rem 0 0 0', color: '#b71c1c' }}>
              Your pick for <strong>{pick.raceName}</strong> includes{' '}
              <strong>{scratchedHorses.map(h => h.horseName).join(', ')}</strong>,
              which {scratchedHorses.length === 1 ? 'has' : 'have'} been scratched.
              {!deadlinePassed ? (
                <> Please update your pick before the deadline:{' '}
                <strong>{new Date(race.pickDeadline).toLocaleString()}</strong>.</>
              ) : (
                <> The pick deadline has passed.​</>  
              )}
            </p>
            {!deadlinePassed && (
              <button
                onClick={() => navigate(`/races/${pick.raceId}?edit=${pick.pickId}`)}
                style={{
                  marginTop: '0.75rem',
                  background: '#c62828',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1.25rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.95rem'
                }}
              >
                ✏️ Update My Pick
              </button>
            )}
          </div>
        );
      })}

      {/* User Stats Card */}
      <div className="card">
        <h3>Your Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', margin: 0 }}>
              ${user?.totalWinnings?.toFixed(2) || '0.00'}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Total Winnings</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {user?.totalPicks || 0}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Total Picks</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>
              {user?.winRate?.toFixed(1) || '0.0'}%
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Win Rate</p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3', margin: 0 }}>
              #{userRank || '-'}
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>Leaderboard Rank</p>
          </div>
        </div>
      </div>

      {/* Pinned Announcements */}
      {pinnedTidbits.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          {pinnedTidbits.map((tidbit) => (
            <div
              key={tidbit.tidbitId}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>📌</span>
                  <h3 style={{ margin: 0, color: 'white' }}>{tidbit.title}</h3>
                </div>
                <button
                  onClick={() => toggleExpand(tidbit.tidbitId)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '0.35rem 0.7rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap',
                    minWidth: '0',
                    width: 'auto',
                    marginLeft: '0.5rem'
                  }}
                >
                  {expandedTidbits[tidbit.tidbitId] ? '▼ Collapse' : '▶ Expand'}
                </button>
              </div>
              {expandedTidbits[tidbit.tidbitId] && (
                <>
                  <div
                    className="tidbit-content"
                    dangerouslySetInnerHTML={{ __html: tidbit.content }}
                    style={{ color: 'rgba(255,255,255,0.95)' }}
                  />
                  <div style={{ marginTop: '1rem', fontSize: '0.85rem', opacity: 0.8 }}>
                    Posted {new Date(tidbit.createdAt).toLocaleDateString()}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Races */}
      <div className="card">
        <h3>Upcoming Races</h3>
        {upcomingRaces.length === 0 ? (
          <p style={{ color: '#666' }}>No upcoming races available.</p>
        ) : (
          <div className="races-grid">
            {upcomingRaces.map((race) => (
              <div
                key={race.raceId}
                className="race-card"
                onClick={() => navigate(`/races/${race.raceId}`)}
              >
                <h3>{race.raceName}</h3>
                <p className="race-info">📍 {race.venue}</p>
                <p className="race-info">
                  📅 {new Date(race.raceDate).toLocaleDateString()} at{' '}
                  {new Date(race.raceDate).toLocaleTimeString()}
                </p>
                <p className="race-info"><RaceCountdown raceDate={race.raceDate} /></p>
                <p className="race-info">🐎 {race.horses?.length || 0} horses</p>
                <span className={`race-status ${race.status}`}>
                  {race.status}
                </span>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => navigate('/races')}
          className="submit-button"
          style={{ marginTop: '1rem' }}
        >
          View All Races
        </button>
      </div>

      {/* Recent Picks */}
      <div className="card">
        <h3>Recent Picks</h3>
        {recentPicks.length === 0 ? (
          <p style={{ color: '#666' }}>You haven't made any picks yet.</p>
        ) : (
          <table className="horses-table">
            <thead>
              <tr>
                <th>Race</th>
                <th>Horses</th>
                <th>Status</th>
                <th>Payout</th>
              </tr>
            </thead>
            <tbody>
              {recentPicks.map((pick) => {
                const horsesList = pick.horses?.map(h => h.horseName).join(', ') || pick.horseName || 'N/A';
                return (
                  <tr key={pick.pickId}>
                    <td>{pick.raceName}</td>
                    <td>
                      <strong>{horsesList}</strong>
                      {pick.actualPayout > 0 && <span style={{ marginLeft: '0.5rem', color: '#4CAF50' }}>🏆</span>}
                    </td>
                    <td>
                      <span className={`race-status ${pick.status}`}>
                        {pick.status}
                      </span>
                    </td>
                    <td>
                      {pick.status === 'completed' ? (
                        <strong style={{ color: pick.actualPayout > 0 ? '#4CAF50' : '#f44336' }}>
                          ${(pick.actualPayout || 0).toFixed(2)}
                        </strong>
                      ) : (
                        <span style={{ color: '#666' }}>
                          {pick.potentialPayout ? `$${pick.potentialPayout.toFixed(2)}` : 'TBD'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <button
          onClick={() => navigate('/my-picks')}
          className="submit-button"
          style={{ marginTop: '1rem' }}
        >
          View All My Picks
        </button>
      </div>
    </div>
  );
}

export default Dashboard;
